/**
 * MarketplaceOverview — Master Vendor Marketplace top tab.
 * Vision-UI-lit dashboard: glass cards · gradient icon tiles · animated
 * count-ups · sparklines · gradient area chart · top-vendors leaderboard
 * · industry mix donut · recent-activity feed · open complaints banner.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Store, Users, DollarSign, Receipt, Flag, Sparkles, Loader2,
  TrendingUp, ArrowRight, Activity,
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip,
} from 'recharts';
import {
  GlassCard, StatCardV2, DashboardBackdrop, GradientAreaChart,
  GradientIconTile, CountUp,
} from '@/components/dashboard/dashboard-ui';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt$ = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const PIE = ['#8B5CF6', '#22D3EE', '#A3E635', '#F59E0B', '#EC4899', '#FB7185'];

export default function MarketplaceOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('masterToken');
        const r = await axios.get(`${API}/master/marketplace/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } catch (e) {
        console.error('overview load failed', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const series = useMemo(() => {
    if (!data?.series_30d) return [];
    return data.series_30d.map((d) => ({
      name: (d.day || '').slice(5), // MM-DD
      gross: d.gross,
      fees:  d.fees,
    }));
  }, [data]);

  // 8-pt sparkline samples from the 30d series
  const sparkFrom = (key) => {
    if (!series.length) return [];
    const step = Math.max(1, Math.floor(series.length / 8));
    const out = [];
    for (let i = 0; i < series.length; i += step) {
      out.push({ x: i, y: Number(series[i][key]) || 0 });
    }
    return out.slice(-8);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-[#8B5CF6]" />
      </div>
    );
  }

  return (
    <div className="relative" data-testid="marketplace-overview">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-8">
        {/* Header */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#A78BFA] font-bold mb-2">
            Marketplace Pulse
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-syne tracking-tight" data-testid="overview-title">
            Vendor Marketplace Overview
          </h1>
          <p className="text-[#A5A8C9] text-sm mt-1.5">
            Every vendor, customer, transaction, and complaint across EntrepreNatives — at a glance.
          </p>
        </div>

        {/* Top complaint alert (only if open complaints > 0) */}
        {data?.feedback?.open_complaints > 0 && (
          <GlassCard className="p-5 border-amber-400/40 bg-gradient-to-br from-amber-500/12 to-amber-500/5" data-testid="overview-complaints-alert">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <GradientIconTile icon={Flag} ramp="amber" size="lg" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-bold mb-0.5">Action needed</p>
                  <p className="text-lg font-bold text-white font-syne">
                    <CountUp value={data.feedback.open_complaints} /> open complaint{data.feedback.open_complaints === 1 ? '' : 's'}
                  </p>
                  <p className="text-xs text-white/65 mt-0.5">
                    {data.feedback.total} total feedback items received from vendors and customers.
                  </p>
                </div>
              </div>
              <a href="/master/complaints" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white text-[#0E0E23] text-xs font-bold uppercase tracking-wider hover:bg-amber-100 transition-all">
                Review now <ArrowRight size={13} />
              </a>
            </div>
          </GlassCard>
        )}

        {/* 6 Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5" data-testid="overview-stats">
          <StatCardV2
            icon={Store}
            label="Total Vendors"
            value={data?.vendors?.total || 0}
            change={data?.vendors?.new_30d || 0}
            positive
            ramp="violet"
            spark={sparkFrom('gross')}
            sparkId="vendors"
            data-testid="ov-stat-vendors"
            delay={0}
          />
          <StatCardV2
            icon={Sparkles}
            label="Active Vendors"
            value={data?.vendors?.active || 0}
            ramp="fuchsia"
            spark={sparkFrom('fees')}
            sparkId="active"
            data-testid="ov-stat-active-vendors"
            delay={0.06}
          />
          <StatCardV2
            icon={Users}
            label="Total Customers"
            value={data?.customers?.total || 0}
            change={data?.customers?.new_30d || 0}
            positive
            ramp="cyan"
            spark={sparkFrom('gross')}
            sparkId="customers"
            data-testid="ov-stat-customers"
            delay={0.12}
          />
          <StatCardV2
            icon={DollarSign}
            label="Platform Revenue"
            value={data?.platform?.platform_revenue || 0}
            format={fmt$}
            ramp="lime"
            spark={sparkFrom('fees')}
            sparkId="revenue"
            data-testid="ov-stat-revenue"
            delay={0.18}
          />
          <StatCardV2
            icon={Receipt}
            label="Transactions"
            value={data?.platform?.transactions || 0}
            ramp="amber"
            spark={sparkFrom('gross')}
            sparkId="tx"
            data-testid="ov-stat-transactions"
            delay={0.24}
          />
          <StatCardV2
            icon={Flag}
            label="Open Complaints"
            value={data?.feedback?.open_complaints || 0}
            ramp={data?.feedback?.open_complaints > 0 ? 'amber' : 'lime'}
            spark={sparkFrom('fees')}
            sparkId="complaints"
            data-testid="ov-stat-complaints"
            delay={0.30}
          />
        </div>

        {/* Gross Volume + Platform Fees chart */}
        <GlassCard glow className="p-6" data-testid="overview-chart">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Activity</p>
              <h3 className="text-lg font-bold text-white font-syne">Marketplace Activity · last 30 days</h3>
              <p className="text-xs text-[#A5A8C9] mt-0.5">Gross volume processed + platform fees collected, by day</p>
            </div>
            <div className="flex items-center gap-4 text-[11px]">
              <span className="inline-flex items-center gap-1.5 text-[#A5A8C9]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#8B5CF6', boxShadow: '0 0 10px rgba(139,92,246,0.7)' }} />
                Gross volume
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#A5A8C9]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#A3E635', boxShadow: '0 0 10px rgba(163,230,53,0.7)' }} />
                Platform fees
              </span>
            </div>
          </div>
          <GradientAreaChart
            data={series}
            height={280}
            series={[
              { key: 'gross', label: 'Gross', ramp: 'violet' },
              { key: 'fees',  label: 'Fees',  ramp: 'lime'   },
            ]}
            yFormatter={fmt$}
          />
        </GlassCard>

        {/* Two-column row: Top vendors + Industry mix */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Vendors leaderboard */}
          <GlassCard glow className="lg:col-span-2 overflow-hidden" data-testid="top-vendors-card">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06]">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Leaderboard</p>
                <h3 className="text-lg font-bold text-white font-syne flex items-center gap-2">
                  <TrendingUp size={16} className="text-[#A78BFA]" /> Top Vendors by Gross Sales
                </h3>
              </div>
              <a href="/master/users" className="text-sm text-[#A78BFA] hover:text-white flex items-center gap-1 transition-colors">
                All users <ArrowRight size={14} />
              </a>
            </div>
            {data?.top_vendors?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th className="px-6 pb-3 pt-4 text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-semibold">Vendor</th>
                      <th className="px-6 pb-3 pt-4 text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-semibold text-right">Gross</th>
                      <th className="px-6 pb-3 pt-4 text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-semibold text-right">Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_vendors.map((v, i) => (
                      <tr key={v.vendor_id} className="border-t border-white/[0.04] hover:bg-white/[0.025] transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {v.profile_image ? (
                              <img src={v.profile_image} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" />
                            ) : (
                              <span
                                className="w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                                style={{
                                  background: `linear-gradient(135deg, ${PIE[i % PIE.length]}, ${PIE[(i + 1) % PIE.length]})`,
                                  boxShadow: `0 6px 18px -6px ${PIE[i % PIE.length]}88`,
                                }}
                              >
                                {(v.business_name || '?').slice(0, 2).toUpperCase()}
                              </span>
                            )}
                            <div>
                              <p className="font-semibold text-white">{v.business_name}</p>
                              <p className="text-xs text-[#7B7FA8]">{v.industry || '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-[#A3E635] tabular-nums">{fmt$(v.gross)}</td>
                        <td className="px-6 py-4 text-right text-[#A5A8C9] tabular-nums">{v.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16 text-[#7B7FA8] text-sm">No vendor sales yet — sales will appear as they come in.</div>
            )}
          </GlassCard>

          {/* Industry mix donut */}
          <GlassCard glow className="p-6" data-testid="industry-mix-card">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Mix</p>
            <h3 className="text-lg font-bold text-white font-syne mb-4">Vendor Industries</h3>
            {data?.industry_mix?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <defs>
                      {PIE.map((c, i) => (
                        <linearGradient key={i} id={`indpie-${i}`} x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                          <stop offset="100%" stopColor={c} stopOpacity={0.55} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={data.industry_mix}
                      cx="50%" cy="50%"
                      innerRadius={42} outerRadius={75}
                      paddingAngle={2}
                      dataKey="count"
                    >
                      {data.industry_mix.map((_, i) => (
                        <Cell key={i} fill={`url(#indpie-${i % PIE.length})`} stroke="#0E0E23" strokeWidth={2} />
                      ))}
                    </Pie>
                    <RTooltip
                      contentStyle={{ background: '#13153A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-3">
                  {data.industry_mix.map((row, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PIE[i % PIE.length], boxShadow: `0 0 8px ${PIE[i % PIE.length]}99` }} />
                        <span className="text-white truncate">{row.label || 'Other'}</span>
                      </div>
                      <span className="text-[#A5A8C9] tabular-nums">{row.count}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[170px] flex items-center justify-center text-[#7B7FA8] text-sm">No vendors yet</div>
            )}
          </GlassCard>
        </div>

        {/* Recent activity feed */}
        <GlassCard glow className="p-6" data-testid="recent-activity-card">
          <div className="flex items-center gap-3 mb-4">
            <GradientIconTile icon={Activity} ramp="cyan" size="sm" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-0.5">Live</p>
              <h3 className="text-lg font-bold text-white font-syne">Recent activity</h3>
            </div>
          </div>
          {data?.recent?.length > 0 ? (
            <ul className="space-y-2">
              {data.recent.map((e, i) => (
                <li key={i} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-white/[0.025] border border-white/[0.05] hover:bg-white/[0.04] transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        background: e.type === 'vendor_joined' ? '#A78BFA' : '#A3E635',
                        boxShadow: `0 0 10px ${e.type === 'vendor_joined' ? 'rgba(167,139,250,0.7)' : 'rgba(163,230,53,0.7)'}`,
                      }}
                    />
                    <p className="text-sm text-white truncate">{e.title}</p>
                  </div>
                  <span className="text-[10px] text-[#7B7FA8] shrink-0">
                    {e.ts ? new Date(e.ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-center py-8 text-[#7B7FA8] text-sm">No activity yet — it'll show up here as vendors join and orders come in.</p>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
